import { Controller } from "@hotwired/stimulus";

const BLOCK = "toolkit-rich-select-component";
const ACTIVE_CLASS = `${BLOCK}__option--active`;
const CHANGE_EVENT = "rich-select:change";
const TYPEAHEAD_RESET_MS = 600;
const MIRRORED_PARTS = ["badge", "title", "tag", "meta"];

/**
 * Rich select: select-only ARIA combobox (focus stays on the trigger, the active option is
 * exposed through aria-activedescendant) with a listbox popover.
 */
export default class extends Controller {
  static targets = ["trigger", "panel", "option", "input", "summary"];

  declare readonly triggerTarget: HTMLButtonElement;
  declare readonly panelTarget: HTMLElement;
  declare readonly optionTargets: HTMLElement[];
  declare readonly inputTarget: HTMLInputElement;
  declare readonly summaryTarget: HTMLElement;

  private activeIndex = -1;
  private typeahead = "";
  private typeaheadTimer: number | null = null;

  private readonly onOutsidePointer = (event: Event): void => {
    if (!this.element.contains(event.target as Node)) this.close();
  };

  // Turbo snapshots the page as-is; never cache it with the panel open.
  private readonly onBeforeCache = (): void => this.close();

  connect(): void {
    this.close();
    this.syncSelection();
    document.addEventListener("turbo:before-cache", this.onBeforeCache);
  }

  disconnect(): void {
    document.removeEventListener("turbo:before-cache", this.onBeforeCache);
    document.removeEventListener("pointerdown", this.onOutsidePointer, true);
    this.clearTypeahead();
  }

  get isOpen(): boolean {
    return !this.panelTarget.hidden;
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open(this.selectedIndex());
    }
  }

  // Keeps focus on the trigger while the pointer interacts with the panel.
  keepFocus(event: Event): void {
    event.preventDefault();
  }

  hover(event: Event): void {
    const index = this.indexOfEventTarget(event);
    if (index >= 0 && index !== this.activeIndex) this.setActive(index, false);
  }

  pick(event: Event): void {
    const index = this.indexOfEventTarget(event);
    if (index >= 0) this.commit(index);
  }

  triggerKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (this.isOpen) {
      this.handleOpenKey(event);
    } else {
      this.handleClosedKey(event);
    }
  }

  close(): void {
    this.panelTarget.hidden = true;
    this.triggerTarget.setAttribute("aria-expanded", "false");
    this.triggerTarget.removeAttribute("aria-activedescendant");
    document.removeEventListener("pointerdown", this.onOutsidePointer, true);
    this.clearTypeahead();
    this.activeIndex = -1;
    this.optionTargets.forEach((option) => option.classList.remove(ACTIVE_CLASS));
  }

  private open(index: number): void {
    this.panelTarget.hidden = false;
    this.triggerTarget.setAttribute("aria-expanded", "true");
    document.addEventListener("pointerdown", this.onOutsidePointer, true);
    this.setActive(index >= 0 ? index : 0, true);
  }

  private handleClosedKey(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case " ":
        event.preventDefault();
        this.open(this.selectedIndex());
        break;
      case "Home":
        event.preventDefault();
        this.open(0);
        break;
      case "End":
        event.preventDefault();
        this.open(this.optionTargets.length - 1);
        break;
      default:
        if (this.isPrintable(event)) {
          event.preventDefault();
          this.open(this.selectedIndex());
          this.searchByTitle(event.key);
        }
    }
  }

  private handleOpenKey(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.setActive(Math.min(this.activeIndex + 1, this.optionTargets.length - 1), true);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.setActive(Math.max(this.activeIndex - 1, 0), true);
        break;
      case "Home":
        event.preventDefault();
        this.setActive(0, true);
        break;
      case "End":
        event.preventDefault();
        this.setActive(this.optionTargets.length - 1, true);
        break;
      case "Escape":
        // Keep an enclosing modal/dialog from also reacting to this Escape.
        event.preventDefault();
        event.stopPropagation();
        this.close();
        break;
      case "Tab":
        this.close();
        break;
      case "Enter":
        event.preventDefault();
        if (this.activeIndex >= 0) this.commit(this.activeIndex);
        break;
      case " ":
        event.preventDefault();
        if (this.typeahead) {
          this.searchByTitle(" ");
        } else if (this.activeIndex >= 0) {
          this.commit(this.activeIndex);
        }
        break;
      default:
        if (this.isPrintable(event)) {
          event.preventDefault();
          this.searchByTitle(event.key);
        }
    }
  }

  private isPrintable(event: KeyboardEvent): boolean {
    return event.key.length === 1;
  }

  private searchByTitle(character: string): void {
    this.typeahead += character.toLowerCase();
    if (this.typeaheadTimer !== null) window.clearTimeout(this.typeaheadTimer);
    this.typeaheadTimer = window.setTimeout(() => this.clearTypeahead(), TYPEAHEAD_RESET_MS);

    // A single (or repeated) character cycles through matches; a longer prefix narrows in place.
    const repeated = this.typeahead.split("").every((c) => c === this.typeahead[0]);
    const needle = repeated ? this.typeahead[0] : this.typeahead;
    const count = this.optionTargets.length;
    const start = repeated ? this.activeIndex + 1 : Math.max(this.activeIndex, 0);

    for (let offset = 0; offset < count; offset++) {
      const index = (start + offset) % count;
      const title = (this.optionTargets[index].dataset.title || "").toLowerCase();
      if (title.startsWith(needle)) {
        this.setActive(index, true);
        return;
      }
    }
  }

  private clearTypeahead(): void {
    this.typeahead = "";
    if (this.typeaheadTimer !== null) {
      window.clearTimeout(this.typeaheadTimer);
      this.typeaheadTimer = null;
    }
  }

  private setActive(index: number, scroll: boolean): void {
    const option = this.optionTargets[index];
    if (!option) return;

    this.optionTargets.forEach((candidate) => candidate.classList.remove(ACTIVE_CLASS));
    option.classList.add(ACTIVE_CLASS);
    this.activeIndex = index;
    this.triggerTarget.setAttribute("aria-activedescendant", option.id);
    if (scroll && typeof option.scrollIntoView === "function") option.scrollIntoView({ block: "nearest" });
  }

  private commit(index: number): void {
    const option = this.optionTargets[index];
    if (!option) return;

    const value = option.dataset.value ?? "";
    const changed = value !== this.inputTarget.value;

    this.inputTarget.value = value;
    this.syncSelection();
    this.close();
    this.triggerTarget.focus();

    if (changed) this.announce(value, option.dataset.title ?? "");
  }

  private announce(value: string, title: string): void {
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.element.dispatchEvent(new CustomEvent(CHANGE_EVENT, { bubbles: true, detail: { value, title } }));
  }

  // Makes aria-selected and the trigger summary reflect the hidden input, with no server round-trip.
  private syncSelection(): void {
    const selected = this.selectedIndex();

    this.optionTargets.forEach((option, index) => {
      option.setAttribute("aria-selected", index === selected ? "true" : "false");
    });

    if (selected >= 0) this.mirrorSummary(this.optionTargets[selected]);
  }

  private mirrorSummary(option: HTMLElement): void {
    const parts: Node[] = [];

    MIRRORED_PARTS.forEach((part) => {
      const source = option.querySelector(`[data-rich-select-part="${part}"]`);
      if (!source) return;

      const copy = source.cloneNode(true) as HTMLElement;
      copy.removeAttribute("id");
      parts.push(copy);
    });

    this.summaryTarget.textContent = "";
    parts.forEach((part) => this.summaryTarget.appendChild(part));
  }

  private selectedIndex(): number {
    const value = this.inputTarget.value;
    return this.optionTargets.findIndex((option) => (option.dataset.value ?? "") === value);
  }

  private indexOfEventTarget(event: Event): number {
    const option = (event.target as Element | null)?.closest<HTMLElement>('[role="option"]');
    return option ? this.optionTargets.indexOf(option) : -1;
  }
}
